/**
 * The composer: one row, and Enter sends.
 *
 * One attach button and an icon-only send beside the field, because that is
 * what the desktop app settled on after its first real two-machine session —
 * three of the five icons it had drawn did nothing, and the hint line under it
 * restated what Enter does.
 *
 * Typing is announced on the ephemeral lane while somebody is writing and
 * withdrawn three seconds after they stop or the moment they send. A client
 * that only ever sets it true leaves a stranger typing forever.
 *
 * `@` opens a picker of `candidates`. The field keeps focus and points at the
 * highlighted person with `aria-activedescendant`; picking writes `@Name ` and
 * records who, and a send carries the recorded people whose name is still in
 * the text (`mention.ts`).
 *
 * The attach button opens Cloud's picker in a popup (`cloud-picker.ts`), and
 * what it hands back waits as removable chips until the send. Files go without
 * text if there is none, as they do from the desktop.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { store } from '../../lib/relay/store';
import {
  MAX_PICKED_FILES, cloudOrigin, openCloudPicker, readPickerMessage, type SendWithFiles
} from '../../lib/relay/cloud-picker';
import type { WireFile } from '../../lib/relay/payload';
import type { Message } from '../../lib/relay/types';
import {
  insertMention, matchMentions, mentionQuery, mentionsToSend, type MentionCandidate
} from './mention';
import './chat.css';

const IDLE_MS = 3000;

/** The message being quoted, if any — the desktop's reply chip. */
export interface Reference {
  message: Message;
  /** The author's display name, resolved by the page. */
  name: string;
}

export function Composer({
  conversationId,
  threadRoot,
  placeholder,
  reference,
  onClearReference,
  candidates
}: {
  conversationId: string;
  /** The thread pane's composer: every message is a reply to this root. */
  threadRoot?: string;
  placeholder?: string;
  reference?: Reference;
  onClearReference: () => void;
  /** Who `@` offers: `mentionCandidates` of the members. */
  candidates: MentionCandidate[];
}) {
  const { IconButton } = ds();
  const t = useT();
  const [draft, setDraft] = useState('');
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<MentionCandidate[]>([]);
  const [active, setActive] = useState(0);
  /** The `@` Escape closed, so the next keystroke does not open it again. */
  const [dismissed, setDismissed] = useState<number | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const moveCaret = useRef<number | null>(null);
  const listId = useId();
  const typing = useRef(false);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [files, setFiles] = useState<WireFile[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  /** The picker this composer opened. Only it may answer, and only once. */
  const picker = useRef<Window | null>(null);

  useEffect(() => {
    const origin = cloudOrigin();
    const onMessage = (event: MessageEvent) => {
      const picked = readPickerMessage(event, { origin, source: picker.current });
      if (!picked) return;
      picker.current = null;
      const next = [...files, ...picked];
      setFiles(next.slice(0, MAX_PICKED_FILES));
      setNotice(
        next.length > MAX_PICKED_FILES
          ? t('conversation.attach.limit', { count: MAX_PICKED_FILES })
          : null
      );
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [files, t]);

  // Synchronous from the click, or the browser blocks the popup.
  const attach = () => {
    picker.current?.close();
    picker.current = openCloudPicker();
    setNotice(picker.current ? null : t('conversation.attach.blocked'));
  };

  const stopTyping = () => {
    clearTimeout(idle.current);
    if (!typing.current) return;
    typing.current = false;
    store.setTyping(conversationId, false);
  };

  // Leaving the conversation with the flag set is the same bug as never
  // clearing it, seen from the other end.
  useEffect(() => stopTyping, [conversationId]);

  const query = mentionQuery(draft, caret);
  const matches = query && query.start !== dismissed ? matchMentions(candidates, query.query) : [];
  const open = matches.length > 0;
  const highlighted = Math.min(active, matches.length - 1);

  // After a pick, once the new text is in the field.
  useLayoutEffect(() => {
    if (moveCaret.current === null || !field.current) return;
    field.current.setSelectionRange(moveCaret.current, moveCaret.current);
    setCaret(moveCaret.current);
    moveCaret.current = null;
  }, [draft]);

  const pick = (candidate: MentionCandidate) => {
    if (!query) return;
    const next = insertMention(draft, query.start, caret, candidate.name);
    moveCaret.current = next.caret;
    setPicked((list) => [...list, candidate]);
    onInput(next.text);
  };

  const onInput = (value: string) => {
    setDraft(value);
    setActive(0);
    if (!typing.current && value) {
      typing.current = true;
      store.setTyping(conversationId, true);
    }
    clearTimeout(idle.current);
    idle.current = setTimeout(stopTyping, IDLE_MS);
  };

  const send = () => {
    const body = draft.trim();
    if (!body && files.length === 0) return;

    const opts: SendWithFiles = {};
    if (files.length > 0) opts.files = files;
    if (threadRoot) opts.replyTo = threadRoot;
    if (reference) opts.quoteId = reference.message.id;
    const mentions = mentionsToSend(body, picked);
    if (mentions.length > 0) opts.mentions = mentions;

    // Applies locally and renders now; the ack clears `pending`.
    store.send(conversationId, body, opts);
    setDraft('');
    setPicked([]);
    setFiles([]);
    setNotice(null);
    stopTyping();
    onClearReference();
  };

  return (
    <div>
      {reference && (
        <div className="rl-chip">
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="rl-truncate rl-chip-name">
              {t('conversation.replying', { name: reference.name })}
            </span>
            <span className="rl-truncate" style={{ display: 'block' }}>
              {reference.message.body}
            </span>
          </span>
          <IconButton
            icon="Close"
            size="sm"
            label={t('conversation.replying.cancel')}
            onClick={onClearReference}
          />
        </div>
      )}

      {files.length > 0 && (
        <div className="rl-files">
          {files.map((file, i) => (
            <span key={`${file.id}-${i}`} className="rl-file-chip">
              <span className="rl-truncate" title={file.x}>{file.x}</span>
              <IconButton
                icon="Close"
                size="sm"
                label={t('conversation.attach.remove', { name: file.x ?? '' })}
                onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}
              />
            </span>
          ))}
        </div>
      )}
      {notice && (
        <p role="status" className="rl-composer-notice">
          {notice}
        </p>
      )}

      <div className="rl-composer">
        {open && (
          <div
            id={listId}
            role="listbox"
            aria-label={t('conversation.mention.list')}
            className="rl-menu rl-mention-picker"
          >
            {matches.map((candidate, i) => (
              <div
                key={candidate.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === highlighted}
                className="rl-menu-item"
                // Down, not click: a click would take focus from the field first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(candidate);
                }}
                onMouseEnter={() => setActive(i)}
              >
                {candidate.name}
              </div>
            ))}
          </div>
        )}
        <IconButton icon="Attach" label={t('conversation.attach')} onClick={attach} />
        <textarea
          ref={field}
          value={draft}
          rows={1}
          placeholder={placeholder ?? t('conversation.composer.placeholder')}
          aria-autocomplete="list"
          {...(open
            ? { 'aria-controls': listId, 'aria-activedescendant': `${listId}-${highlighted}` }
            : {})}
          onChange={(e) => {
            const at = e.target.selectionStart;
            if (mentionQuery(e.target.value, at)?.start !== dismissed) setDismissed(null);
            setCaret(at);
            onInput(e.target.value);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={(e) => {
            if (open && !e.nativeEvent.isComposing) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const step = e.key === 'ArrowDown' ? 1 : -1;
                setActive((highlighted + step + matches.length) % matches.length);
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                pick(matches[highlighted]!);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setDismissed(query!.start);
                return;
              }
            }
            // Only when there is a quote to drop: an unconditional Escape would
            // take the key from whatever else wants it.
            if (e.key === 'Escape' && reference) {
              e.preventDefault();
              onClearReference();
              return;
            }
            // Shift+Enter is a newline. So is the Enter that commits an IME
            // candidate — it is a keystroke aimed at the word being composed,
            // not at the message, and sending on it posts half a sentence to
            // everybody typing Japanese, Chinese or Korean.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton icon="Send" label={t('conversation.send')} variant="primary" onClick={send} />
      </div>
    </div>
  );
}
